/**
 * Provider Detail Page
 *
 * Displays and edits provider details and click-out configuration.
 * Capability-gated to users with products:read.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Package,
  Loader2,
  AlertCircle,
  RefreshCw,
  Save,
  Plus,
  Trash2,
  Home,
  ChevronRight,
} from "lucide-react";
import type { ReactElement, ChangeEvent } from "react";
import { useState, useCallback, useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";

import { apiRequest } from "../../api/client";
import { ProviderStatusBadge } from "../../components/providers/ProviderStatusBadge";
import { useAuthStore } from "../../store/authStore";

// ============================================================================
// Types
// ============================================================================

/** Provider response shape from API */
interface ProviderResponse {
  readonly id: string;
  readonly externalId: string;
  readonly name: string;
  readonly slug: string;
  readonly status: "active" | "inactive" | "archived" | "deleted";
  readonly metadata: {
    readonly logoUrl?: string;
    readonly websiteUrl?: string;
    readonly clickOutUrlTemplate?: string;
    readonly contactEmail?: string;
    readonly trackingParams?: Readonly<Record<string, string>>;
    readonly description?: string;
  };
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Tab values */
type DetailTab = "details" | "clickout";

/** Editable form state for details tab */
interface DetailsFormState {
  name: string;
  description: string;
  contactEmail: string;
  websiteUrl: string;
  logoUrl: string;
  status: "active" | "inactive" | "archived";
}

/** Tracking param entry */
interface TrackingParam {
  readonly key: string;
  readonly value: string;
}

/** Editable form state for click-out tab */
interface ClickoutFormState {
  clickOutUrlTemplate: string;
  trackingParams: TrackingParam[];
}

// ============================================================================
// Main Component
// ============================================================================

export function ProviderDetailPage(): ReactElement {
  const { hasCapability } = useAuthStore();

  if (!hasCapability("products:read")) {
    return <Navigate to="/studio" replace />;
  }

  return <ProviderDetailContent />;
}

function ProviderDetailContent(): ReactElement {
  const { verticalKey, providerId } = useParams<{
    verticalKey: string;
    providerId: string;
  }>();
  const queryClient = useQueryClient();
  const { hasCapability } = useAuthStore();

  const canWrite = hasCapability("products:write");

  const [activeTab, setActiveTab] = useState<DetailTab>("details");
  const [detailsForm, setDetailsForm] = useState<DetailsFormState>({
    name: "",
    description: "",
    contactEmail: "",
    websiteUrl: "",
    logoUrl: "",
    status: "active",
  });
  const [clickoutForm, setClickoutForm] = useState<ClickoutFormState>({
    clickOutUrlTemplate: "",
    trackingParams: [],
  });
  const [detailsDirty, setDetailsDirty] = useState(false);
  const [clickoutDirty, setClickoutDirty] = useState(false);

  // ==========================================================================
  // Data Fetching
  // ==========================================================================

  const {
    data: provider,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["provider", providerId],
    queryFn: async () => {
      const response = await apiRequest<ProviderResponse>(`/api/v1/providers/${providerId}`);
      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    enabled: Boolean(providerId),
  });

  // Populate forms from fetched data
  useEffect(() => {
    if (!provider) return;
    setDetailsForm({
      name: provider.name,
      description: provider.metadata.description ?? "",
      contactEmail: provider.metadata.contactEmail ?? "",
      websiteUrl: provider.metadata.websiteUrl ?? "",
      logoUrl: provider.metadata.logoUrl ?? "",
      status: provider.status === "deleted" ? "archived" : provider.status,
    });
    setClickoutForm({
      clickOutUrlTemplate: provider.metadata.clickOutUrlTemplate ?? "",
      trackingParams: Object.entries(provider.metadata.trackingParams ?? {}).map(
        ([key, value]) => ({ key, value })
      ),
    });
    setDetailsDirty(false);
    setClickoutDirty(false);
  }, [provider]);

  // ==========================================================================
  // Update Mutation
  // ==========================================================================

  const updateMutation = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const response = await apiRequest<ProviderResponse>(`/api/v1/providers/${providerId}`, {
        method: "PATCH",
        body,
      });
      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["provider", providerId] });
      queryClient.invalidateQueries({ queryKey: ["providers"] });
      setDetailsDirty(false);
      setClickoutDirty(false);
    },
  });

  // ==========================================================================
  // Details Form Handlers
  // ==========================================================================

  const handleDetailsChange = useCallback(
    (field: keyof DetailsFormState) =>
      (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
        setDetailsForm((prev) => ({ ...prev, [field]: e.target.value }));
        setDetailsDirty(true);
      },
    []
  );

  const handleDetailsSave = useCallback((): void => {
    const metadata: Record<string, unknown> = {};
    if (detailsForm.description) metadata["description"] = detailsForm.description;
    if (detailsForm.contactEmail) metadata["contactEmail"] = detailsForm.contactEmail;
    if (detailsForm.websiteUrl) metadata["websiteUrl"] = detailsForm.websiteUrl;
    if (detailsForm.logoUrl) metadata["logoUrl"] = detailsForm.logoUrl;

    const body: Record<string, unknown> = {
      name: detailsForm.name,
      status: detailsForm.status,
      metadata,
    };

    updateMutation.mutate(body);
  }, [detailsForm, updateMutation]);

  // ==========================================================================
  // Click-out Form Handlers
  // ==========================================================================

  const handleClickoutTemplateChange = useCallback((e: ChangeEvent<HTMLInputElement>): void => {
    setClickoutForm((prev) => ({ ...prev, clickOutUrlTemplate: e.target.value }));
    setClickoutDirty(true);
  }, []);

  const handleTrackingParamChange = useCallback(
    (index: number, field: "key" | "value", value: string): void => {
      setClickoutForm((prev) => {
        const updated = [...prev.trackingParams];
        const existing = updated[index];
        if (existing) {
          updated[index] = { ...existing, [field]: value };
        }
        return { ...prev, trackingParams: updated };
      });
      setClickoutDirty(true);
    },
    []
  );

  const handleAddTrackingParam = useCallback((): void => {
    setClickoutForm((prev) => ({
      ...prev,
      trackingParams: [...prev.trackingParams, { key: "", value: "" }],
    }));
    setClickoutDirty(true);
  }, []);

  const handleRemoveTrackingParam = useCallback((index: number): void => {
    setClickoutForm((prev) => ({
      ...prev,
      trackingParams: prev.trackingParams.filter((_, i) => i !== index),
    }));
    setClickoutDirty(true);
  }, []);

  const handleClickoutSave = useCallback((): void => {
    // URL scheme validation: only allow http:// and https://
    const template = clickoutForm.clickOutUrlTemplate.trim();
    if (template && !/^https?:\/\//i.test(template)) {
      updateMutation.reset();
      return;
    }

    // Tracking param key validation: alphanumeric, underscore, hyphen only
    const safeKeyPattern = /^[a-zA-Z0-9_-]+$/;
    const trackingParams: Record<string, string> = {};
    for (const param of clickoutForm.trackingParams) {
      const key = param.key.trim();
      if (key) {
        if (!safeKeyPattern.test(key)) {
          return;
        }
        trackingParams[key] = param.value;
      }
    }

    const metadata: Record<string, unknown> = {};
    if (template) {
      metadata["clickOutUrlTemplate"] = template;
    }
    if (Object.keys(trackingParams).length > 0) {
      metadata["trackingParams"] = trackingParams;
    }

    updateMutation.mutate({ metadata });
  }, [clickoutForm, updateMutation]);

  // ==========================================================================
  // Render
  // ==========================================================================

  if (isLoading) {
    return (
      <div className="flex h-full flex-col">
        <Breadcrumb verticalKey={verticalKey} />
        <div className="flex flex-col items-center justify-center flex-1 py-12 text-foreground-muted">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p className="mt-4">Loading provider...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full flex-col">
        <Breadcrumb verticalKey={verticalKey} />
        <div className="flex flex-col items-center justify-center flex-1 py-12">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-error/10">
            <AlertCircle className="h-6 w-6 text-error" />
          </div>
          <p className="mt-4 text-foreground-muted">
            {error instanceof Error ? error.message : "Failed to load provider"}
          </p>
          <button onClick={() => refetch()} className="btn btn-secondary mt-4">
            <RefreshCw className="mr-2 h-4 w-4" />
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="flex h-full flex-col">
        <Breadcrumb verticalKey={verticalKey} />
        <div className="flex items-center justify-center flex-1 text-foreground-muted">
          Provider not found
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <Breadcrumb verticalKey={verticalKey} providerName={provider.name} />

      <div className="flex-1 overflow-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <Link
            to={`/studio/${verticalKey}/providers`}
            className="inline-flex items-center gap-1 text-sm text-foreground-muted hover:text-foreground mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Providers
          </Link>

          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              {provider.metadata.logoUrl ? (
                <img
                  src={provider.metadata.logoUrl}
                  alt={`${provider.name} logo`}
                  className="h-14 w-14 rounded-lg object-contain bg-background border border-border"
                />
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Package className="h-7 w-7" />
                </div>
              )}
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold">{provider.name}</h1>
                  <ProviderStatusBadge status={provider.status} />
                </div>
                <p className="mt-1 text-sm text-foreground-muted font-mono">{provider.slug}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Update error */}
        {updateMutation.error instanceof Error && (
          <div className="mb-4 rounded-md border border-error/30 bg-error/10 p-3 text-sm text-error">
            {updateMutation.error.message}
          </div>
        )}

        {/* Update success */}
        {updateMutation.isSuccess && (
          <div className="mb-4 rounded-md border border-success/30 bg-success/10 p-3 text-sm text-success">
            Provider updated successfully
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6 flex gap-1 border-b border-border">
          <button
            onClick={() => setActiveTab("details")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "details"
                ? "border-primary text-primary"
                : "border-transparent text-foreground-muted hover:text-foreground"
            }`}
          >
            Details
          </button>
          <button
            onClick={() => setActiveTab("clickout")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "clickout"
                ? "border-primary text-primary"
                : "border-transparent text-foreground-muted hover:text-foreground"
            }`}
          >
            Click-out Config
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === "details" ? (
          <DetailsTab
            form={detailsForm}
            isDirty={detailsDirty}
            isSaving={updateMutation.isPending}
            canWrite={canWrite}
            onChange={handleDetailsChange}
            onSave={handleDetailsSave}
          />
        ) : (
          <ClickoutTab
            form={clickoutForm}
            isDirty={clickoutDirty}
            isSaving={updateMutation.isPending}
            canWrite={canWrite}
            onTemplateChange={handleClickoutTemplateChange}
            onParamChange={handleTrackingParamChange}
            onAddParam={handleAddTrackingParam}
            onRemoveParam={handleRemoveTrackingParam}
            onSave={handleClickoutSave}
          />
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Breadcrumb
// ============================================================================

interface BreadcrumbProps {
  readonly verticalKey: string | undefined;
  readonly providerName?: string;
}

function Breadcrumb({ verticalKey, providerName }: BreadcrumbProps): ReactElement {
  return (
    <div className="flex items-center gap-2 border-b border-border bg-background-secondary px-4 py-2 text-sm">
      <Link
        to="/studio"
        className="flex items-center gap-1 text-foreground-muted hover:text-foreground"
      >
        <Home className="h-4 w-4" />
        <span>Studio</span>
      </Link>
      <ChevronRight className="h-4 w-4 text-foreground-muted" />
      <Link to={`/studio/${verticalKey}`} className="text-foreground-muted hover:text-foreground">
        {verticalKey}
      </Link>
      <ChevronRight className="h-4 w-4 text-foreground-muted" />
      <Link
        to={`/studio/${verticalKey}/providers`}
        className="text-foreground-muted hover:text-foreground"
      >
        Providers
      </Link>
      {providerName && (
        <>
          <ChevronRight className="h-4 w-4 text-foreground-muted" />
          <span className="font-medium">{providerName}</span>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Details Tab
// ============================================================================

interface DetailsTabProps {
  readonly form: DetailsFormState;
  readonly isDirty: boolean;
  readonly isSaving: boolean;
  readonly canWrite: boolean;
  readonly onChange: (
    field: keyof DetailsFormState
  ) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  readonly onSave: () => void;
}

function DetailsTab({
  form,
  isDirty,
  isSaving,
  canWrite,
  onChange,
  onSave,
}: DetailsTabProps): ReactElement {
  return (
    <div className="card max-w-2xl space-y-4">
      {/* Name */}
      <div>
        <label htmlFor="detail-name" className="block text-sm font-medium mb-1">
          Name
        </label>
        <input
          id="detail-name"
          type="text"
          value={form.name}
          onChange={onChange("name")}
          disabled={!canWrite}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
        />
      </div>

      {/* Slug (read-only) */}
      <div>
        <label className="block text-sm font-medium mb-1">Slug</label>
        <p className="px-3 py-2 text-sm font-mono text-foreground-muted bg-background-secondary rounded-md border border-border">
          Read-only after creation
        </p>
      </div>

      {/* Description */}
      <div>
        <label htmlFor="detail-description" className="block text-sm font-medium mb-1">
          Description
        </label>
        <textarea
          id="detail-description"
          value={form.description}
          onChange={onChange("description")}
          rows={3}
          disabled={!canWrite}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
        />
      </div>

      {/* Contact Email */}
      <div>
        <label htmlFor="detail-email" className="block text-sm font-medium mb-1">
          Contact Email
        </label>
        <input
          id="detail-email"
          type="email"
          value={form.contactEmail}
          onChange={onChange("contactEmail")}
          disabled={!canWrite}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
        />
      </div>

      {/* Website URL */}
      <div>
        <label htmlFor="detail-website" className="block text-sm font-medium mb-1">
          Website URL
        </label>
        <input
          id="detail-website"
          type="url"
          value={form.websiteUrl}
          onChange={onChange("websiteUrl")}
          disabled={!canWrite}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
        />
      </div>

      {/* Logo URL */}
      <div>
        <label htmlFor="detail-logo" className="block text-sm font-medium mb-1">
          Logo URL
        </label>
        <input
          id="detail-logo"
          type="url"
          value={form.logoUrl}
          onChange={onChange("logoUrl")}
          disabled={!canWrite}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
        />
      </div>

      {/* Status */}
      <div>
        <label htmlFor="detail-status" className="block text-sm font-medium mb-1">
          Status
        </label>
        <select
          id="detail-status"
          value={form.status}
          onChange={onChange("status")}
          disabled={!canWrite}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {/* Save */}
      {canWrite && (
        <div className="pt-2">
          <button onClick={onSave} disabled={!isDirty || isSaving} className="btn btn-primary">
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Click-out Config Tab
// ============================================================================

interface ClickoutTabProps {
  readonly form: ClickoutFormState;
  readonly isDirty: boolean;
  readonly isSaving: boolean;
  readonly canWrite: boolean;
  readonly onTemplateChange: (e: ChangeEvent<HTMLInputElement>) => void;
  readonly onParamChange: (index: number, field: "key" | "value", value: string) => void;
  readonly onAddParam: () => void;
  readonly onRemoveParam: (index: number) => void;
  readonly onSave: () => void;
}

function ClickoutTab({
  form,
  isDirty,
  isSaving,
  canWrite,
  onTemplateChange,
  onParamChange,
  onAddParam,
  onRemoveParam,
  onSave,
}: ClickoutTabProps): ReactElement {
  return (
    <div className="card max-w-2xl space-y-6">
      {/* Click-out URL Template */}
      <div>
        <label htmlFor="clickout-template" className="block text-sm font-medium mb-1">
          Click-out URL Template
        </label>
        <input
          id="clickout-template"
          type="text"
          value={form.clickOutUrlTemplate}
          onChange={onTemplateChange}
          placeholder="https://example.com/go?provider={providerId}&vertical={verticalKey}"
          disabled={!canWrite}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
        />
        <p className="mt-1 text-xs text-foreground-muted">
          Available placeholders: {"{providerId}"}, {"{verticalKey}"}, {"{productId}"},{" "}
          {"{tenantId}"}
        </p>
      </div>

      {/* Tracking Parameters */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium">Tracking Parameters</label>
          {canWrite && (
            <button
              onClick={onAddParam}
              className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80"
            >
              <Plus className="h-3.5 w-3.5" />
              Add Parameter
            </button>
          )}
        </div>

        {form.trackingParams.length === 0 ? (
          <p className="text-sm text-foreground-muted py-4 text-center">
            No tracking parameters configured
          </p>
        ) : (
          <div className="space-y-2">
            {form.trackingParams.map((param, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  value={param.key}
                  onChange={(e) => onParamChange(index, "key", e.target.value)}
                  placeholder="Key"
                  disabled={!canWrite}
                  className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm font-mono focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                />
                <input
                  type="text"
                  value={param.value}
                  onChange={(e) => onParamChange(index, "value", e.target.value)}
                  placeholder="Value"
                  disabled={!canWrite}
                  className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm font-mono focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                />
                {canWrite && (
                  <button
                    onClick={() => onRemoveParam(index)}
                    className="p-2 text-error hover:bg-error/10 rounded transition-colors"
                    title="Remove parameter"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Save */}
      {canWrite && (
        <div className="pt-2">
          <button onClick={onSave} disabled={!isDirty || isSaving} className="btn btn-primary">
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Click-out Config
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
